import KpiChart from "@app/components/kpi-chart";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { seatHistoryChart_Query } from "@app/queries/seatHistoryChart_Query.graphql";

const seatQuery = graphql`
  query seatHistoryChart_Query($pseudonym: String!) {
    customerSeatHistory(pseudonym: $pseudonym) {
      pseudonym
      available
      changed
      minSeats
      maxSeats
      currentSeats
      points {
        month
        seats
        observed
        plan
      }
    }
  }
`;

export default function SeatHistoryChart({
  pseudonym,
  onClose,
}: {
  pseudonym: string;
  onClose: () => void;
}) {
  const data = useLazyLoadQuery<seatHistoryChart_Query>(
    seatQuery,
    { pseudonym },
    { fetchPolicy: "store-or-network" }
  );

  const history = data.customerSeatHistory;

  return (
    <div className="border-b border-gray-200 bg-white px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">
            {history.pseudonym} — seats over time
          </h3>
          {history.available ? (
            <p className="mt-0.5 text-xs text-gray-500">
              {history.changed
                ? `Moved between ${history.minSeats} and ${history.maxSeats} seats · now ${history.currentSeats}`
                : `Flat at ${history.currentSeats} seat${
                    history.currentSeats === 1 ? "" : "s"
                  } throughout`}{" "}
              · seats billed beyond the base plan, so 0 means the base plan
              alone
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-gray-500">
              No invoice has ever stated a seat count for this customer, so
              there is nothing to plot. That is why their seat band reads
              Unknown — it is an absence, not a zero.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded px-2 py-1 text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          Hide
        </button>
      </div>

      {history.available && (
        <>
          <div className="mt-2">
            <KpiChart
              title=""
              format="number"
              showPointSymbols
              onMonthClick={() => undefined}
              series={[
                {
                  name: "Seats billed",
                  type: "line",
                  points: history.points.map((point) => ({
                    month: point.month,
                    value: point.seats,
                  })),
                },
              ]}
            />
          </div>
          {/* Which months were actually billed a count, and which inherited one.
              Without this the line looks uniformly observed, and a flat stretch
              between two invoices is indistinguishable from a measured plateau. */}
          <p className="mt-1 text-xs text-gray-400">
            {history.points.filter((point) => point.observed).length} of{" "}
            {history.points.length} months were billed a seat count directly;
            the rest carry the last one billed.
          </p>
        </>
      )}
    </div>
  );
}
